// This file will define the initial state and action of our app
import React, { useReducer } from "react";
import axios from "axios";
import GaugeContext from "./gaugeContext";
import GaugeReducer from "./gaugeReducer";
import {
	FETCH_NODES,
	ENABLE_NODE,
	SET_LOADING,
	REMOVE_NODE,
	COLOR_PICKER,
	UPDATE_NAMES,
	SELECT_NODE,
	NODE_COLOR,
	UPDATE_LOG_SCALE_MIN_MAX,
	AVG_NODE_DATA,
} from "../types";

const get_max_and_min_value_of_all_dimensions = (data) => {
	const max_value_1 = data.reduce(
		(max, node) =>
			node["I/O throughput [MiB/s]"] > max
				? node["I/O throughput [MiB/s]"]
				: max,
		data[0]["I/O throughput [MiB/s]"]
	);
	const max_value_2 = data.reduce(
		(max, node) =>
			node["I/O volume [GiB]"] > max ? node["I/O volume [GiB]"] : max,
		data[0]["I/O volume [GiB]"]
	);
	const max_value_3 = data.reduce(
		(max, node) => (node["Runtime [s]"] > max ? node["Runtime [s]"] : max),
		data[0]["Runtime [s]"]
	);
	const max_value_4 = data.reduce(
		(max, node) =>
			node["R/W accesses (in 1000s)"] > max
				? node["R/W accesses (in 1000s)"]
				: max,
		data[0]["R/W accesses (in 1000s)"]
	);
	const max_value_5 = data.reduce(
		(max, node) =>
			node["App size (nprocs)"] > max ? node["App size (nprocs)"] : max,
		data[0]["App size (nprocs)"]
	);
	const max_value_6 = data.reduce(
		(max, node) =>
			node["Files (count)"] > max ? node["Files (count)"] : max,
		data[0]["Files (count)"]
	);

	// Calc min Value
	const min_value_1 = data.reduce(
		(min, node) =>
			node["I/O throughput [MiB/s]"] < min
				? node["I/O throughput [MiB/s]"]
				: min,
		data[0]["I/O throughput [MiB/s]"]
	);
	const min_value_2 = data.reduce(
		(min, node) =>
			node["I/O volume [GiB]"] < min ? node["I/O volume [GiB]"] : min,
		data[0]["I/O volume [GiB]"]
	);
	const min_value_3 = data.reduce(
		(min, node) => (node["Runtime [s]"] < min ? node["Runtime [s]"] : min),
		data[0]["Runtime [s]"]
	);
	const min_value_4 = data.reduce(
		(min, node) =>
			node["R/W accesses (in 1000s)"] < min
				? node["R/W accesses (in 1000s)"]
				: min,
		data[0]["R/W accesses (in 1000s)"]
	);
	const min_value_5 = data.reduce(
		(min, node) =>
			node["App size (nprocs)"] < min ? node["App size (nprocs)"] : min,
		data[0]["App size (nprocs)"]
	);
	const min_value_6 = data.reduce(
		(min, node) =>
			node["Files (count)"] < min ? node["Files (count)"] : min,
		data[0]["Files (count)"]
	);

	return [
		Math.min(
			min_value_1,
			min_value_2,
			min_value_3,
			min_value_4,
			min_value_5,
			min_value_6
		),
		Math.max(
			max_value_1,
			max_value_2,
			max_value_3,
			max_value_4,
			max_value_5,
			max_value_6
		),
	];
};

const get_min_max_of_all_columns = (columns) => {
	let max = 1;
	let min = 1;
	for (let i = 0; i < columns.length; i++) {
		let min_max = get_max_and_min_value_of_all_dimensions(
			columns[i].lfeatures
		);
		if (max < min_max[1]) {
			max = min_max[1];
		}

		if (min > min_max[0]) {
			min = min_max[0];
		}
	}

	return [min, max];
};

const structAppAndUserData = (data) => {
	// Structure the user or app data to the specification of the BarChart
	// Our BarChart will have catagorical data on the left axis and linear data on the bottom axis
	let structData = [];
	for (let i in data) {
		structData.push({ key: i, value: data[i] });
	}

	// Here we are limiting the user data to only 5 entries. The rest will be in a manual computed field named "other"
	let fixedData = [];
	if (structData.length > 5) {
		fixedData = structData.slice(0, 4); // Top 4 entries
		let temp = structData.slice(5, structData.length);
		let sum = temp.reduce(function (a, b) {
			return typeof a != "object" ? a + b.value : a.value + b.value;
		}, 0);
		temp = [];
		fixedData.push({ key: "other", value: sum }); // add the agregated "other" field to the data
	} else {
		fixedData = structData;
	}
	return fixedData;
};

const structParallelPerc = (data) => {
	let structData = [];
	for (let v in data) {
		structData.push({
			"Read ratio (by access)": data[v][0],
			"Read-only files (by file #)": data[v][1],
			"Read-write files (by file #)": data[v][2],
			"Write-only files (by file #)": data[v][3],
			"Unique files (by file #)": data[v][4],
			user: data[v][5],
			app: data[v][7],
		});
	}
	return structData;
};

const structParallelLog = (data) => {
	let structData = [];
	for (let v in data) {
		structData.push({
			"I/O throughput [MiB/s]": data[v][0],
			"I/O volume [GiB]": data[v][1] / 1024 ** 3,
			"Runtime [s]": data[v][2],
			"R/W accesses (in 1000s)": data[v][3],
			"App size (nprocs)": data[v][4],
			"Files (count)": data[v][5],
			user: data[v][6],
			app: data[v][8],
		});
	}
	return structData;
};

const structAverageAccess = (reads, writes) => {
	let reads_avgs = [];
	for (let i = 0; i < 10; i++) {
		let sum = reads.reduce(function (prev, cur) {
			return prev + cur[i] * 100;
		}, 0);
		reads_avgs.push(sum / reads.length);
	}

	let writes_avgs = [];
	for (let i = 0; i < 10; i++) {
		let sum = writes.reduce(function (prev, cur) {
			return prev + cur[i] * 100;
		}, 0);
		writes_avgs.push(sum / writes.length);
	}

	return [
		{
			group: "0-100",
			reads: reads_avgs[0],
			writes: writes_avgs[0],
		},
		{
			group: "101-1k",
			reads: reads_avgs[1],
			writes: writes_avgs[1],
		},
		{
			group: "1k-10k",
			reads: reads_avgs[2],
			writes: writes_avgs[2],
		},
		{
			group: "10k-100k",
			reads: reads_avgs[3],
			writes: writes_avgs[3],
		},
		{
			group: "100k-1M",
			reads: reads_avgs[4],
			writes: writes_avgs[4],
		},
		{
			group: "1M-4M",
			reads: reads_avgs[5],
			writes: writes_avgs[5],
		},
		{
			group: "4M-10M",
			reads: reads_avgs[6],
			writes: writes_avgs[6],
		},
		{
			group: "10M-100M",
			reads: reads_avgs[7],
			writes: writes_avgs[7],
		},
		{
			group: "100M-1G",
			reads: reads_avgs[8],
			writes: writes_avgs[8],
		},
		{
			group: "1G+",
			reads: reads_avgs[9],
			writes: writes_avgs[9],
		},
	];
};

const GaugeState = (props) => {
	const initialState = {
		nodes: [],
		nodesInUse: [],
		loading: false,
		colorBy: "user",
		names: ["Delta", "Gamma", "Beta", "Alpha"],
		nodeColor: "epsilon",
		log_scale_min_max: [1, 1],
		avgNodeData: [],
	};

	const [state, dispatch] = useReducer(GaugeReducer, initialState);

	// FETCH NODES
	const fetchNodes = async () => {
		setLoading();
		const res = await axios.get("/hierarchy");
		dispatch({ type: FETCH_NODES, payload: res.data.nodes });
	};

	// Enable a node to be rendered
	const enableNode = (node) => {
		const apps_request = axios.get(
			`/cluster/${node.index}/count/apps_short`
		);
		const users_request = axios.get(`/cluster/${node.index}/count/users`);
		const percF_request = axios.get(
			`/cluster/${node.index}/percentage_coordinates`
		);
		const logF_request = axios.get(
			`/cluster/${node.index}/log_coordinates`
		);
		const scatF_request = axios.get(
			`/cluster/${node.index}/features/RUNTIME_LOG10+POSIX_READS_PERC`
		);
		const reads_request = axios.get(
			`/cluster/${node.index}/features/POSIX_SIZE_READ_0_100_PERC+POSIX_SIZE_READ_100_1K_PERC+POSIX_SIZE_READ_1K_10K_PERC+POSIX_SIZE_READ_10K_100K_PERC+POSIX_SIZE_READ_100K_1M_PERC+POSIX_SIZE_READ_1M_4M_PERC+POSIX_SIZE_READ_4M_10M_PERC+POSIX_SIZE_READ_10M_100M_PERC+POSIX_SIZE_READ_100M_1G_PERC+POSIX_SIZE_READ_1G_PLUS_PERC`
		);
		const writes_request = axios.get(
			`/cluster/${node.index}/features/POSIX_SIZE_WRITE_0_100_PERC+POSIX_SIZE_WRITE_100_1K_PERC+POSIX_SIZE_WRITE_1K_10K_PERC+POSIX_SIZE_WRITE_10K_100K_PERC+POSIX_SIZE_WRITE_100K_1M_PERC+POSIX_SIZE_WRITE_1M_4M_PERC+POSIX_SIZE_WRITE_4M_10M_PERC+POSIX_SIZE_WRITE_10M_100M_PERC+POSIX_SIZE_WRITE_100M_1G_PERC+POSIX_SIZE_WRITE_1G_PLUS_PERC`
		);

		axios
			.all([
				apps_request,
				users_request,
				percF_request,
				logF_request,
				scatF_request,
				reads_request,
				writes_request,
			])
			.then(
				axios.spread((...responses) => {
					const apps = responses[0];
					const users = responses[1];
					const percF = responses[2];
					const logF = responses[3];
					const scatF = responses[4];
					const reads = responses[5];
					const writes = responses[6];

					const obj = {
						index: node.index,
						users: structAppAndUserData(users.data),
						apps: structAppAndUserData(apps.data),
						lfeatures: structParallelLog(logF.data.data),
						pfeatures: structParallelPerc(percF.data.data),
						scatF: scatF.data,
						rw_access: structAverageAccess(reads.data, writes.data),
						name: node.name,
					};
					let nodes = state.nodesInUse;
					nodes.push(obj);
					dispatch({ type: ENABLE_NODE, payload: nodes });
					nodes = [];
				})
			)
			.catch((errors) => {
				console.log("Problem with sending asynchronous requests");
			});
	};

	// REMOVE NODE
	const removeNode = (index, name) => {
		let nodes = state.nodesInUse;
		let deletedNode = nodes.findIndex((item) => item.index === index);

		nodes.splice(deletedNode, 1);

		updateNames(name);
		dispatch({ type: REMOVE_NODE, payload: nodes });
	};

	// GET A NAME
	const getName = () => {
		let name = state.names.pop();
		return name;
	};

	// UPDATE NAME LIST
	const updateNames = (name) => {
		let names = state.names;
		names.push(name);
		dispatch({ type: UPDATE_NAMES, payload: names });
	};

	// REMOVE NAME
	const setColor = (opt) => {
		dispatch({ type: COLOR_PICKER, payload: opt });
	};

	// SET NODE COLOR
	const setNodeColor = (text) => {
		dispatch({ type: NODE_COLOR, payload: text });
	};

	// SET LOADING
	const setLoading = () => {
		dispatch({ type: SET_LOADING });
	};

	const selectNode = (node) => {
		dispatch({ type: SELECT_NODE, payload: node });
	};

	const cal_nodesInUse_log_scale_min_max = () => {
		if (state.nodesInUse < 1) {
			return;
		}

		let min_max = get_min_max_of_all_columns(state.nodesInUse);
		dispatch({ type: UPDATE_LOG_SCALE_MIN_MAX, payload: min_max });
	};

	// This feature is no longer needed since we now have an api endpoint
	// TODO - code clean up: remove this feature from TreeContainer, GuageReducer, types, and here.
	const fetchAverageFeatureValues = async (feature) => {
		let indexes = state.nodes.map((d) => d.index);
		let requests = indexes.map((d) =>
			axios.get(`/cluster/${d}/average_features/${feature}`)
		);

		let res = [];
		await axios
			.all(requests)
			.then(
				axios.spread((...responses) => {
					for (let i = 0; i < indexes.length; i++) {
						res.push({
							index: indexes[i],
							avg: responses[i].data[0],
						});
					}
					dispatch({ type: AVG_NODE_DATA, payload: res });
				})
			)

			.catch((errors) => {
				// react on errors.
			});
	};

	return (
		<GaugeContext.Provider
			value={{
				nodes: state.nodes,
				loading: state.loading,
				colorBy: state.colorBy,
				nodesInUse: state.nodesInUse,
				names: state.names,
				currentNode: state.currentNode,
				nodeColor: state.nodeColor,
				log_scale_min_max: state.log_scale_min_max,
				avgNodeData: state.avgNodeData,
				fetchNodes,
				enableNode,
				removeNode,
				setColor,
				getName,
				selectNode,
				setNodeColor,
				cal_nodesInUse_log_scale_min_max,
				fetchAverageFeatureValues,
			}}
		>
			{props.children}
		</GaugeContext.Provider>
	);
};

export default GaugeState;
